import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Send, Phone, User, MessageSquare, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";

const PHONE_NUMBER = "+18135083663";
const PHONE_DISPLAY = "(813) 508-3663";
const EMAIL = "contact@pascoappliance.com";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    fullName: "",
    phoneNumber: "",
    message: "",
    honeypot: ""
  });
  const phoneDigits = useRef(""); // Store raw digits in a ref (not state)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Check honeypot - if filled, it's a bot
      if (formData.honeypot) {
        console.log('Honeypot triggered - spam detected');
        // Silently fail for bots
        setTimeout(() => {
          setIsSubmitting(false);
          setIsSubmitted(true);
          setTimeout(() => {
            setIsSubmitted(false);
            setFormData({ fullName: "", phoneNumber: "", message: "", honeypot: "" });
            phoneDigits.current = ""; // Clear phone digits ref
          }, 3000);
        }, 2000);
        return;
      }

      // Send to Make.com webhook → Google Sheets
      const makeWebhookUrl = "https://hook.us2.make.com/1124bgmswt8fyjq6j2o4ktr6g0gusav6";
      
      console.log('Sending to Make.com:', {
        name: formData.fullName,
        phone: formData.phoneNumber,
        message: formData.message
      });
      
      // Try to send to Make.com
      try {
    const response = await fetch(makeWebhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: formData.fullName,
        phone: formData.phoneNumber,
        message: formData.message
      })
    });

    if (response.ok) {
      setIsSubmitted(true);
      setFormData({ fullName: "", phoneNumber: "", message: "", honeypot: "" });
      alert("Thank you! Alex will contact you shortly.");
    } else {
      alert("Oops! Something went wrong. Please call us at (656) 232-2002.");
    }
  } catch (error) {
    console.error("Submit error:", error);
    alert("Oops! Something went wrong. Please call us at (656) 232-2002.");
  } finally {
    setIsSubmitting(false);
  }

      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ fullName: "", phoneNumber: "", message: "", honeypot: "" });
      }, 3000);
    } catch (error) {
      // Even on outer error, show success for Twilio A2P compliance
      console.error('Outer catch - form submission error:', error);
      
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ fullName: "", phoneNumber: "", message: "", honeypot: "" });
        phoneDigits.current = "";
      }, 3000);
    }
  };

  const formatPhoneNumber = (digits) => {
    if (digits.length === 0) return "";
    let formatted = "+1 (" + digits.slice(0, 3);
    if (digits.length >= 4) {
      formatted += ") " + digits.slice(3, 6);
    }
    if (digits.length >= 7) {
      formatted += "-" + digits.slice(6, 10);
    }
    return formatted;
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    
    // Extract only digits
    let digits = value.replace(/\D/g, '');
    
    // Удаляем "1" если пользователь удалил всё, оставив только prefix
    if (digits === '1') {
      digits = '';
    } else if (digits.length > 1 && digits[0] === '1') {
      // Если первая цифра "1" и есть больше цифр, убираем её (это из "+1")
      digits = digits.slice(1);
    }
    
    // Limit to 10 digits
    digits = digits.slice(0, 10);
    
    // Store raw digits
    phoneDigits.current = digits;
    
    // Format and update state
    setFormData({
      ...formData,
      phoneNumber: formatPhoneNumber(digits)
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  return (
    <section className="py-14 sm:py-18 bg-white" id="contact">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Left Column - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="lg:sticky lg:top-24">
              <h2 className="text-3xl sm:text-4xl font-semibold text-[color:var(--navy)] tracking-[-0.02em] mb-4">
                Get a Free Quote
              </h2>
              <p className="text-base text-slate-600 mb-8">
                Fill out the form and we'll get back to you as soon as possible. Same-day service available!
              </p>

              <div className="space-y-6">
                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 h-12 w-12 rounded-full bg-[color:var(--steel)] flex items-center justify-center">
                    <Phone className="h-5 w-5 text-[color:var(--navy)]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[color:var(--navy)] mb-1">Call or Text</h3>
                    <a 
                      href={`tel:${PHONE_NUMBER}`}
                      className="text-[color:var(--safety-orange)] hover:underline font-medium"
                    >
                      {PHONE_DISPLAY}
                    </a>
                    <p className="text-sm text-slate-500 mt-1">Mon-Fri: 9am-5pm | Sat: 9am-8pm</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 h-12 w-12 rounded-full bg-[color:var(--steel)] flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-[color:var(--navy)]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[color:var(--navy)] mb-1">Email Us</h3>
                    <a 
                      href={`mailto:${EMAIL}`}
                      className="text-[color:var(--safety-orange)] hover:underline"
                    >
                      {EMAIL}
                    </a>
                    <p className="text-sm text-slate-500 mt-1">We'll respond within 24 hours</p>
                  </div>
                </div>

                {/* Service Areas */}
                <Card className="border-2 border-[color:var(--steel)] bg-[color:var(--steel)]/30">
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-[color:var(--navy)] mb-2">Service Areas</h3>
                    <p className="text-sm text-slate-600">
                      Pasco, North Hillsborough, North Pinellas
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Card className="border-2 border-slate-200 shadow-[0_10px_40px_rgba(11,31,59,0.08)]">
              <CardContent className="p-6 sm:p-8">
                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot field - hidden from users, visible to bots */}
                    <div className="hidden" aria-hidden="true">
                      <label htmlFor="website">Website</label>
                      <input
                        type="text"
                        id="website"
                        name="honeypot"
                        value={formData.honeypot}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    {/* Full Name */}
                    <div>
                      <label 
                        htmlFor="fullName" 
                        className="block text-sm font-medium text-[color:var(--navy)] mb-2"
                      >
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <Input
                          id="fullName"
                          name="fullName"
                          type="text"
                          required
                          value={formData.fullName}
                          onChange={handleChange}
                          placeholder="John Smith"
                          className="pl-10 h-12 border-slate-300 focus:border-[color:var(--navy)] focus:ring-[color:var(--navy)]"
                        />
                      </div>
                    </div>

                    {/* Phone Number */}
                    <div>
                      <label 
                        htmlFor="phoneNumber" 
                        className="block text-sm font-medium text-[color:var(--navy)] mb-2"
                      >
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <Input
                          id="phoneNumber"
                          name="phoneNumber"
                          type="tel"
                          required
                          value={formData.phoneNumber}
                          onChange={handlePhoneChange}
                          placeholder="+1 (555) 123-4567"
                          className="pl-10 h-12 border-slate-300 focus:border-[color:var(--navy)] focus:ring-[color:var(--navy)]"
                        />
                      </div>
                      <p className="mt-1 text-xs text-slate-500">
                        US numbers only (+1 is automatically added)
                      </p>
                      {/* CRITICAL TWILIO A2P 10DLC COMPLIANCE TEXT - OPTIONAL CHECKBOX */}
                      <div className="mt-2 flex items-start gap-3 p-3 bg-slate-50 rounded-md border border-slate-200">
                        <input
                          type="checkbox"
                          id="smsConsent"
                          name="smsConsent"
                          className="mt-1 h-4 w-4 text-[color:var(--navy)] border-slate-300 rounded focus:ring-[color:var(--navy)]"
                        />
                        <label htmlFor="smsConsent" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                          I agree to receive service-related text messages from Pasco Appliance LLC. Consent is not a condition of purchase or service. Reply STOP to opt-out. Message and data rates may apply.
                        </label>
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label 
                        htmlFor="message" 
                        className="block text-sm font-medium text-[color:var(--navy)] mb-2"
                      >
                        Tell us about your appliance issue
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="My washer stopped working, making a loud noise, model number GE1234..."
                        rows={5}
                        className="resize-none border-slate-300 focus:border-[color:var(--navy)] focus:ring-[color:var(--navy)]"
                      />
                      <p className="mt-2 text-xs text-slate-500">
                        Tip: Include your appliance model number if you have it
                      </p>
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full h-12 bg-[color:var(--safety-orange)] text-white hover:bg-[#ff4a12] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--safety-orange-2)] shadow-[0_4px_14px_rgba(255,90,31,0.25)] transition-all font-semibold"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-5 w-5 mr-2" />
                          Submit Request
                        </>
                      )}
                    </Button>

                    {/* reCAPTCHA notice (required by Google) */}
                    <p className="text-xs text-slate-500 text-center">
                      This site is protected by reCAPTCHA and the Google{' '}
                      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a>{' '}
                      and{' '}
                      <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms of Service</a>{' '}
                      apply.
                    </p>
                  </form>
                ) : (
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="py-12 text-center"
                  >
                    <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-[color:var(--navy)] mb-2">
                      Request Received!
                    </h3>
                    <p className="text-slate-600">
                      We'll contact you shortly to schedule your repair.
                    </p>
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
